import type { AppMode } from '@iheartjane/types';
import { configureStore } from '@reduxjs/toolkit';

import { setNotificationsDispatch } from '@jane/monolith/services/notifications';
import { identifyCustomerUserAtFirstOpportunity } from '@jane/monolith/utils/identifyAtFirstOpportunity';
import { setOnUnauthorized } from '@jane/monolith/utils/request';
import type { AnalyticsIntegration } from '@jane/shared/types';
import { setAppMode } from '@jane/shared/util';

import { addressAutocompleteReducer } from './redux/addressAutocomplete';
import {
  addGoogleAnalyticsInstance,
  applicationReducer,
} from './redux/application';
import { bundlePossibilitiesReducer } from './redux/bundlePossibilities';
import { cartReducer, persistCartToLocalStorage } from './redux/cart';
import { customerReducer, handleInvalidLoginToken } from './redux/customer';
import { embeddedAppReducer } from './redux/embeddedApp';
import { identificationReducer } from './redux/identification';
import { initialQueryReducer } from './redux/initialQuery';
import { loginReducer } from './redux/login';
import { modalReducer } from './redux/modal';
import { notificationsReducer } from './redux/notifications';
import { operatorEmbeddedReducer } from './redux/operatorEmbedded';
import { posCartReducer } from './redux/posCart';
import { productReducer } from './redux/product';
import { storeReducer } from './redux/store';
import { usersReducer } from './redux/users';

export interface ExtraArgsRef {
  current: {
    appMode: AppMode;
    disableGaInBloomHeadless?: boolean;
    headlessStoreId?: string;
  };
}

export const configureCustomerStore = (
  preloadedState: object = {},
  extraArgsRef: ExtraArgsRef
) => {
  const store = configureStore({
    reducer: {
      addressAutocomplete: addressAutocompleteReducer,
      application: applicationReducer,
      bundlePossibilities: bundlePossibilitiesReducer,
      cart: cartReducer,
      customer: customerReducer,
      embeddedApp: embeddedAppReducer,
      identification: identificationReducer,
      initialQuery: initialQueryReducer,
      login: loginReducer,
      modal: modalReducer,
      notifications: notificationsReducer,
      operatorEmbedded: operatorEmbeddedReducer,
      posCart: posCartReducer,
      product: productReducer,
      store: storeReducer,
      users: usersReducer,
    },
    preloadedState,
    middleware: (getDefaultMiddleware) => {
      return getDefaultMiddleware({
        thunk: {
          extraArgument: extraArgsRef,
        },
      });
    },
  });

  setAppModeForRoutes(store, extraArgsRef);
  setOnUnauthorized(() => store.dispatch(handleInvalidLoginToken(true)));
  setNotificationsDispatch(store.dispatch);
  identifyCustomerUserAtFirstOpportunity(store);
  persistCartToLocalStorage(store, extraArgsRef.current.appMode);

  return store;
};
export type AppStore = ReturnType<typeof configureCustomerStore>;

export type AppState = ReturnType<AppStore['getState']>;
export type AppDispatch = AppStore['dispatch'];

const setAppModeForRoutes = (
  reduxStore: ReturnType<typeof configureCustomerStore>,
  extraArgsRef: ExtraArgsRef
) => {
  reduxStore.subscribe(() => {
    const { appMode, disableGaInBloomHeadless } = extraArgsRef.current;
    const { operatorEmbedded, embeddedApp, store, application } =
      reduxStore.getState();
    const { partnerId } = embeddedApp;
    const { operatorId } = operatorEmbedded;
    const storeStore = store.store ?? undefined;
    let analyticsIntegration: AnalyticsIntegration | undefined;
    if (storeStore?.analytics_integration) {
      analyticsIntegration = { ...storeStore.analytics_integration };
    }

    switch (appMode) {
      case 'default':
        setAppMode({ type: appMode });
        break;
      case 'embedded':
      case 'whiteLabel':
      case 'framelessEmbed':
        if (partnerId != null) {
          setAppMode({ partnerId, type: appMode });
        }
        break;
      case 'operatorEmbed':
        if (operatorId != null && partnerId != null) {
          setAppMode({ operatorId, storeId: partnerId, type: appMode });
        }
        break;
      case 'headless':
        setAppMode({ type: appMode });
        break;
      case 'bloom':
        setAppMode({ type: appMode });
        break;
    }

    // Bloom pages track GA4 themselves; the checkout iframe would start a second client.
    const gaExcludedAppModes: AppMode[] = disableGaInBloomHeadless
      ? ['default', 'bloom']
      : ['default'];

    if (
      analyticsIntegration &&
      analyticsIntegration.provider_id &&
      analyticsIntegration.enabled &&
      application.googleAnalyticsInstance !==
        analyticsIntegration.provider_id &&
      !gaExcludedAppModes.includes(appMode)
    ) {
      reduxStore.dispatch(
        addGoogleAnalyticsInstance({
          trackingId: analyticsIntegration.provider_id,
        })
      );
    }
  });
};
